import React from 'react';
import { Navigate } from 'react-router-dom';
import { LEGACY_PATH_REDIRECTS } from './paths.js';
import { NotFoundRoute } from '../layout/ProtectedAppLayout.jsx';
import {
  AuditoriaRoute,
  BillingRoute,
  CockpitRoute,
  ConfigRoute,
  DashboardRoute,
  EquipeRoute,
  InadimplenciaRoute,
  IntegrationsRoute,
  KnowledgeBaseRoute,
  LojaDetailRoute,
  LojasRoute,
  NotificacoesOpsRoute,
  NovaLojaRoute,
  ObservabilidadeRoute,
  PerfisRoute,
  PlanosPermissoesRoute,
  PlanosRoute,
  RepairsRoute,
  SupportReportsRoute,
  TechSpecRoute,
  TicketsRoute,
} from './pages.jsx';

/**
 * Single source of truth for the Super Admin route tree (Phase 4).
 * `router.jsx` wraps this with session-aware containers (auth, layout);
 * this module owns only path → element wiring.
 */

export function LegacyRedirect({ to }) {
  return <Navigate to={to} replace />;
}

/** Root + `:tabSegment` pair for a module (Phase 2 subroutes). */
export function withTabSegment(basePath, element) {
  return [
    { path: basePath, element },
    { path: `${basePath}/:tabSegment`, element },
  ];
}

/** `[from, to]` pairs for every legacy path alias — single source (`paths.js`). */
export const legacyRedirectEntries = Object.entries(LEGACY_PATH_REDIRECTS);

/**
 * Children rendered inside the authenticated shell (`ProtectedAppLayout`).
 * The parent route already wraps this subtree with `RequireAuth`, so no
 * individual route here needs its own auth check.
 */
export function buildProtectedRouteChildren() {
  const cockpitEl = <CockpitRoute />;
  const billingEl = <BillingRoute />;
  const inadEl = <InadimplenciaRoute />;
  const integEl = <IntegrationsRoute />;
  const obsEl = <ObservabilidadeRoute />;
  const repairsEl = <RepairsRoute />;
  const ticketsEl = <TicketsRoute />;
  const kbEl = <KnowledgeBaseRoute />;
  const reportsEl = <SupportReportsRoute />;
  const notifEl = <NotificacoesOpsRoute />;
  const auditoriaEl = <AuditoriaRoute />;

  return [
    { path: '/dashboard', element: <DashboardRoute /> },
    { path: '/lojas', element: <LojasRoute /> },
    { path: '/lojas/nova', element: <NovaLojaRoute /> },
    { path: '/lojas/:tenantId', element: <LojaDetailRoute /> },
    { path: '/lojas/:tenantId/cockpit', element: cockpitEl },
    { path: '/lojas/:tenantId/cockpit/:tabSegment', element: cockpitEl },
    { path: '/planos', element: <PlanosRoute /> },
    { path: '/planos/permissoes', element: <PlanosPermissoesRoute /> },
    ...withTabSegment('/inadimplencia', inadEl),
    // Billing redirects before :tabSegment.
    { path: '/billing/inadimplencia', element: <LegacyRedirect to="/inadimplencia" /> },
    { path: '/billing/regua', element: <LegacyRedirect to="/inadimplencia" /> },
    ...withTabSegment('/billing', billingEl),
    { path: '/equipe', element: <EquipeRoute /> },
    { path: '/equipe/perfis', element: <PerfisRoute /> },
    { path: '/configuracoes', element: <ConfigRoute /> },
    { path: '/auditoria', element: auditoriaEl },
    { path: '/auditoria/:eventId', element: auditoriaEl },
    ...withTabSegment('/integracoes', integEl),
    ...withTabSegment('/observabilidade', obsEl),
    ...withTabSegment('/repairs', repairsEl),
    ...withTabSegment('/suporte/tickets', ticketsEl),
    // KB article editor before the generic :tabSegment.
    { path: '/suporte/base-conhecimento/artigos/novo', element: kbEl },
    ...withTabSegment('/suporte/base-conhecimento', kbEl),
    ...withTabSegment('/suporte/relatorios', reportsEl),
    ...withTabSegment('/notificacoes', notifEl),
    { path: '/sistema/especificacao-tecnica', element: <TechSpecRoute /> },
    // Legacy admin paths (inside the protected tree so they beat the splat).
    { path: '/admin/support/tickets', element: <LegacyRedirect to="/suporte/tickets" /> },
    { path: '/admin/support/tickets/:ticketId', element: <LegacyRedirect to="/suporte/tickets" /> },
    { path: '/admin/support/knowledge-base', element: <LegacyRedirect to="/suporte/base-conhecimento" /> },
    { path: '/admin/support/knowledge-base/articles/:articleId', element: <LegacyRedirect to="/suporte/base-conhecimento" /> },
    { path: '/admin/support/reports', element: <LegacyRedirect to="/suporte/relatorios" /> },
    { path: '/admin/billing', element: <LegacyRedirect to="/billing" /> },
    { path: '/admin/observability', element: <LegacyRedirect to="/observabilidade" /> },
    { path: '*', element: <NotFoundRoute /> },
  ];
}
